/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import XDate from 'xdate';
import {AMAZON_AF_DEFAULT_DISK_IMAGE} from '~/common/affiliate/amazon';
import {
  UPDATE_GIFT_OPTIONS,
  UPDATE_PLEDGELING_OPTIONS,
} from '~/common/fabric/reducers/giftingOptions/constants';
import {PLAN_NAME_PARTY_PASS, PLAN_NAME_PRO} from '~/common/fabric/routes/EventReview/constants';
import {getTimezone, isEmpty} from '~/common/utils';
import {
  TURN_OFF_DONATION_LOADING,
  TURN_ON_DONATION_LOADING,
  UPDATE_INVITATION_SUMMARY,
} from '~/ViewInvitation/reducers/constants';

const EVENT_TITLE = 'event_title';
const EVENT_MODULE = 'event_details';
const WHO_IS_COMING_MODULE = 'who_is_coming';
const GIFT_REGISTRY_MODULE = 'gift_registry';
const GIFTING_MODULE = 'gifting';
const DONATION_MODULE = 'donation';
const POLL_MODULE = 'poll';
const WHAT_TO_BRING_MODULE = 'what_to_bring';
const HOST_PHOTO_MODULE = 'host_photo';
const PHOTO_MODULE = 'photo';

const displayOrder = [
  [EVENT_TITLE, 1],
  [EVENT_MODULE, 2],
  [HOST_PHOTO_MODULE, 3],
  [WHO_IS_COMING_MODULE, 4],
  [PHOTO_MODULE, 5],
  [GIFT_REGISTRY_MODULE, 6],
  [GIFTING_MODULE, 7],
  [DONATION_MODULE, 8],
  [POLL_MODULE, 9],
  [WHAT_TO_BRING_MODULE, 10],
];

export const previewTransformMiddleware = (store) => (next) => (action) => {
  next(action);
  const state = store.getState();
  const event = {...state.eviteEvent, rsvp: undefined};
  if (Object.keys(state.eviteEvent).length === 0) return state;
  const reminders = [];
  if (event.event_reminder_0) {
    reminders.push(event.event_reminder_0);
  }
  if (event.event_reminder_1) {
    reminders.push(event.event_reminder_1);
  }
  const pathPieces = window.location.pathname.split('/').slice(1);
  const inStep2 = pathPieces[2] === 'details';
  state.eventDetails = {
    ...state.eventDetails,
    ...state.details,
    ...state.details.address,
    title: state.details.title || '',
    locationName: state.details.location,
    streetAddress: state.details.address?.street ?? null,
    zipCode: state.details.address?.zip || null,
    start: state.details.start || '',
    end: state.details.end || '',
    messageFromHost: state.details.notes,
    hostName: state.details.host || event.host_name,
    _evite_event: event,
    shortLink: event.shortlink,
    eventId: event.id,
    eventType: event.event_type,
    status: event.status,
    usedAs: event.used_as,
    rsvpStyle: {...event.rsvp_style},
    allowGifts: !!state.giftingOptions.options.event_option_show_gifting,
    registriesData: event.gift_registries_data,
    fabric_design_data: event.fabric_design_data,
    design: event.options.design,
    allowGuests: event.options.event_option_allow_guest_number,
    allowInviteMore: event.options.event_option_allow_invite_more,
    allowKids: event.options.event_option_allow_kids,
    maxGuests: event.options.event_option_max_guest_number || 100,
    maxTotalGuests: event.options.event_option_max_guests || 750,
    privateGuestlist: event.options.event_option_private_guestlist,
    hasPassed: window.event_has_passed || false,
    origin: event.origin,
    isPremium: state.details.isPremium ?? (event.origin === 13 || event.is_fabric_premium),
    removeAds: event.remove_ads || false,
    reminders,
    pledgeling_data: {
      pledgeling_added: event.pledgeling_added,
      pledgeling_beneficiary: event.pledgeling_beneficiary,
      pledgeling_beneficiary_type: event.pledgeling_beneficiary_type,
      pledgeling_category: event.pledgeling_category,
      pledgeling_goal: event.pledgeling_goal,
      pledgeling_id: event.pledgeling_id,
      pledgeling_purpose: event.pledgeling_purpose,
    },
    videoId: event.event_videos_id,
    pristineHostMessage: !state.details.notes && inStep2,
  };

  const {
    address: {city, state: addrState, zip, street},
    location: locationName,
  } = state.details;
  state.eventDetails.editedLocation = inStep2
    ? !!locationName || !!street || !!city || !!addrState || !!zip
    : true;
  if (!state.eventDetails.editedLocation) {
    state.eventDetails.streetAddress = '';
    state.eventDetails.city = '';
    state.eventDetails.state = '';
    state.eventDetails.zipCode = '';
  } else {
    state.eventDetails.streetAddress = street;
    state.eventDetails.city = city;
    state.eventDetails.state = addrState;
    state.eventDetails.zipCode = zip;
  }

  const premiumDesign = event.options.design;
  state.premiumDesign = {};
  if (
    premiumDesign &&
    (state.eventDetails.isPremium ||
      [PLAN_NAME_PRO, PLAN_NAME_PARTY_PASS].includes(state.user.subscriptionPlanName))
  ) {
    const {fabricDesign, fabricTemplate} = state;

    const freemiumEnvelope = fabricTemplate?.allEnvelopes?.find(
      ({id}) => id === fabricDesign?.envelopeId
    );
    const freemiumLiner = fabricTemplate?.allLiners?.find(({id}) => id === fabricDesign?.linerId);
    const freemiumStamp = fabricTemplate?.allStamps?.find(({id}) => id === fabricDesign?.stampId);

    state.premiumDesign = {
      envelope: {
        name: {
          ...premiumDesign.name_style,
        },
        openBack: freemiumEnvelope?.blank || premiumDesign.envelope_flap_up,
        closedBack: freemiumEnvelope?.flap_down_image || premiumDesign.envelope_flap_down,
        closedFront: freemiumEnvelope?.image || premiumDesign.envelope_front,
      },
      stamp: {
        image:
          freemiumStamp?.media?.find(({label}) => label === 'image')?.url ||
          premiumDesign.stamp_image,
      },
      background: {
        background: premiumDesign.background,
        color: premiumDesign.background_color || fabricDesign?.backgroundColor,
        imageDesktop: premiumDesign.background_image_desktop,
        imageMobile: premiumDesign.background_image_mobile,
      },
      liner: {
        liner: premiumDesign.liner,
        image:
          freemiumLiner?.media?.find(({label}) => label === 'image')?.url ||
          premiumDesign.liner_image,
      },
      envelopeStickers: premiumDesign.envelope_fabric_stickers || fabricDesign?.envelopeStickers, // Freemium edits are on fabricDesign
    };
  }

  const timezone = getTimezone();
  state.details.known_timezone_abbr = state.details.known_timezone_abbr || timezone?.timezoneAbbr;
  state.details.known_timezone_name = state.details.known_timezone_name || timezone?.timezoneId;

  state.coverData = {
    known_timezone_abbr: state.details.known_timezone_abbr,
    known_timezone_name: state.details.known_timezone_name,
    template: {},
  };

  state.user = {
    ...state.user,
    isHost: state.eventDetails.host_ids.indexOf(state.user.userId) > -1,
  };

  const rsvp = event.rsvp || {};
  state.rsvp = {
    hasRsvpd: false,
    guestEmail: rsvp?.email ?? '',
  };

  state.gifting =
    action.type === UPDATE_INVITATION_SUMMARY
      ? {
          url: action.results?.gifting?.url ?? '',
          iconUrl: action.results?.gifting?.icon_url ?? '',
          vendorName: 'Amazon',
        }
      : {
          url: state.gifting?.url ?? '',
          iconUrl: state.gifting?.iconUrl ?? AMAZON_AF_DEFAULT_DISK_IMAGE,
          vendorName: state.gifting?.vendorName ?? 'Amazon',
        };

  state.registry =
    action.type === UPDATE_INVITATION_SUMMARY
      ? {
          display_order: 3,
          name: action.results.gift_registry?.name || '',
          note: action.results.gift_registry?.registry_note || '',
          registries:
            action.results.gift_registry?.registries.map((gf) => ({
              ...gf,
              vendorName: gf.name,
            })) ?? [],
        }
      : {
          ...state.registry,
          display_order: state.registry?.display_order ?? 3,
          name: state.registry?.name || state.giftingOptions.gift_registry_name || '',
          registries: state.registry?.registries ?? [],
        };
  state.giftKeywords = {
    keywords: [],
  };
  state.whatToBring = {
    items: [],
    allowOther: false,
    creatorId: state.user.userId,
  };
  state.whatToBringSummary = {
    participants: [],
    listId: '',
  };
  state.guestList = {
    rsvpCounts: {
      adults_yes: 0,
      adults_maybe: 0,
      kids_yes: 0,
      kids_maybe: 0,
      yes: 0,
      maybe: 0,
      no: 0,
      no_reply: 0,
    },
    rsvpDetails: {
      maybe: [],
      yes: [],
      no_reply: [],
      no: [],
    },
  };
  // TODO: whoIsComing
  state.whoIsComing = {
    isPrivateGuestlist: state.giftingOptions.options.event_option_private_guestlist,
    guestPreviews: [
      {
        userId: 1,
        adults: state.eventDetails.allowKids ? 2 : 3,
        guestName: 'Guest Name',
        kids: 1,
        rsvpComment: 'Guest comment',
        rsvpWhen: XDate(new Date()).addDays(-7).toString(),
      },
      {
        userId: 2,
        adults: state.eventDetails.allowKids ? 2 : 3,
        guestName: 'Guest Name',
        kids: 1,
        rsvpComment: '',
        rsvpWhen: XDate(new Date()).addDays(-6).toString(),
      },
      {
        userId: 3,
        adults: state.eventDetails.allowKids ? 1 : 2,
        guestName: 'Guest Name',
        kids: 1,
        rsvpComment: 'Guest comment',
        rsvpWhen: XDate(new Date()).addDays(-5).toString(),
      },
      {
        userId: 4,
        adults: state.eventDetails.allowKids ? 1 : 2,
        guestName: 'Guest Name',
        kids: 1,
        rsvpComment: '',
        rsvpWhen: XDate(new Date()).addDays(-4).toString(),
      },
      {
        userId: 5,
        adults: 1,
        guestName: 'Guest Name',
        kids: 0,
        rsvpComment: '',
        rsvpWhen: XDate(new Date()).addDays(-3).toString(),
      },
    ],
  };
  // TODO: donation
  state.donation = {
    ...state.donation,
    loading: state.donation?.loading ?? false,
    donations: state.donation?.donations ?? [],
  };
  if (
    action.type === UPDATE_INVITATION_SUMMARY ||
    action.type === UPDATE_PLEDGELING_OPTIONS ||
    action.type === UPDATE_GIFT_OPTIONS
  ) {
    state.donation.donations = [];
    if (!isEmpty(state.giftingOptions?.pledgeling_data)) {
      if ('pledgeling_beneficiary_type' in state.giftingOptions.pledgeling_data.Organization) {
        state.donation.donations.push({
          id: `${event.id}_Organization`,
          donation_url: `https://${window.pledge_url}/widgets/donate/${window.pledge_key}/${event.id}_Organization`,
          found: true,
        });
      }
      if ('pledgeling_beneficiary_type' in state.giftingOptions.pledgeling_data.Crowdfund) {
        state.donation.donations.push({
          id: `${event.id}_Crowdfund`,
          donation_url: `https://${window.pledge_url}/widgets/donate/${window.pledge_key}/${event.id}_Crowdfund`,
          found: true,
        });
      }
    }
  } else if (action.type === TURN_ON_DONATION_LOADING) {
    state.donation.loading = true;
  } else if (action.type === TURN_OFF_DONATION_LOADING) {
    state.donation.loading = false;
  }
  state.eventDetails.hasDonations = state.donation.donations.length > 0;

  state.poll =
    state.widgets?.polls?.length > 0
      ? {
          ...state.widgets?.polls[0],
        }
      : [];

  const moduleSet = new Set(displayOrder.map((o) => o[0]));
  if (
    state.eventDetails.eventType === 'funeral_memorial_service' ||
    (!state.eventDetails.allowGifts &&
      state.registry.registries.length === 0 &&
      !state?.giftingOptions?.force_show_module)
  ) {
    // Hide gifting module if event is a funeral / memorial service, OR if user has turned off registries and gifting
    moduleSet.delete(GIFTING_MODULE);
  }

  if (state.registry.registries.length === 0) {
    moduleSet.delete(GIFT_REGISTRY_MODULE);
  }

  if (state.eventDetails.rsvp_off) {
    moduleSet.delete(WHO_IS_COMING_MODULE);
  }

  if (!state.widgets?.whatToBrings?.length > 0) {
    moduleSet.delete(WHAT_TO_BRING_MODULE);
  }

  if (!state.giftingOptions?.options?.event_option_enable_host_photo_gallery) {
    moduleSet.delete(HOST_PHOTO_MODULE);
  }

  if (!state.eventDetails.hasDonations) {
    moduleSet.delete(DONATION_MODULE);
  }

  if (!state.widgets?.polls?.length) {
    moduleSet.delete(POLL_MODULE);
  }

  state.moduleOrder = {
    displayOrder: Array.from(moduleSet),
  };

  next(action);
  return state;
};
